import type { Pixel } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { Executor } from "@pdfking/planner"
import type { Page, PageNumber } from "@pdfking/renderer-api"
import { GroupMatrix, PlotFunction } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import type { ExecutionContext } from "@pdfking/taskmaker"
import { AtomHash, GuidToString } from "@pdfking/tfunc"

import type { BinaryPagePdf } from "#public"
import type { FilePlottingContext } from "#public"
import type { PdfEngineOptions } from "#public"
import type { SinglePagePdf } from "#public"
import type { TasksType } from "#tasks"

import { FileContentGUID } from "../../public/methods/FileContentGUID"
import { VirtualFileType } from "../../public/types/files/VirtualFileType"

type Planner = Pick<Executor<TasksType, ExecutionContext>, "plan">

const MEDIA = "page" as FileName

// A split page as a plot: placed whole on a page of its size, its bleed shown
// out to the file bleed the raster is drawn with
const PagePlot = (
  options: PdfEngineOptions,
  source: BinaryPagePdf
): { page: Page; context: FilePlottingContext } => ({
  page: {
    ...source.trim,
    bleed: options.file_bleed,
    shapes: [
      {
        enum: PlotFunction.PLOT_MEDIA,
        filename: MEDIA,
        mask: options.file_bleed,
        page_number: 1 as PageNumber,
        position: { ...source.trim, x: ZERO_DISTANCE, y: ZERO_DISTANCE },
      },
    ],
  },
  context: {
    file_map: { [MEDIA]: { [1 as PageNumber]: { matrix: GroupMatrix(), page: source } } },
  },
})

/** The plan that rasterizes `source` at `dpi`: TaskPlot over its recipe — a
 * plotted page's own, a split page's PagePlot. Named after the page and the
 * resolution, so a re-run lands where the first did. */
export const RasterNode = (
  { plan }: Planner,
  options: PdfEngineOptions,
  source: SinglePagePdf,
  dpi: Pixel
) => {
  const { page, context } =
    source.enum === VirtualFileType.PLOTTED_PAGE_PDF ? source : PagePlot(options, source)
  return plan.TaskPlot({
    options,
    page,
    context,
    dpi,
    hash: GuidToString(AtomHash([VirtualFileType.SINGLE_PAGE_IMAGE, FileContentGUID(source), dpi])),
  })
}
